import { DatePipe } from '@angular/common';
import { Component, effect, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { TableStateService } from '@core/services/table-state.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DatatableBaseComponent } from '@shared/components/datatable-base/datatable-base.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDatatableConfiguration,
  IRowClickEvent,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { combineLatest, filter } from 'rxjs';
import { ICustomer, ICustomerTable } from '../customers.interface';
import { CustomersStore } from '../customers.store';

@Component({
  imports: [
    HasPermissionDirective,
    MatButtonModule,
    MatInputModule,
    MatMenuModule,
    TranslateModule,
    MatDialogModule,
    RouterModule,
    IconBadgeComponent,
    DatatableComponent,
    IconComponent,
    DatePipe,
  ],
  selector: 'app-customers-datatable',
  styleUrl: './customers-datatable.component.scss',
  templateUrl: './customers-datatable.component.html',
})
export class CustomersDatatableComponent extends DatatableBaseComponent implements OnInit {
  public readonly customers = input.required<ICustomer[] | null>();
  public readonly customersLoading = input.required<boolean>();

  public readonly displayedColumns: string[] = ['customer', 'created', 'campaigns', 'actions'];

  public readonly loadedTableData = signal<boolean>(false);
  public readonly isRedirecting = signal<boolean>(false);
  public readonly dataSource = signal<MatTableDataSource<ICustomerTable>>(new MatTableDataSource<ICustomerTable>([]));

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '4px',
        circleSize: '40px',
        className: 'flex-1 w-[65%]',
        columnPadding: '0 8px',
        hasText: true,
        rowPadding: '8px 0',
        textWidth: '120px',
        type: 'icon',
      },
      {
        borderRadius: '4px',
        circleSize: '16px',
        className: 'w-[13%]',
        hasText: true,
        textWidth: '80px',
        type: 'icon',
      },
      {
        borderRadius: '4px',
        circleSize: '16px',
        className: 'w-[15%]',
        hasText: true,
        textWidth: '30px',
        type: 'icon',
      },
      {
        borderRadius: '100vw',
        circleSize: '24px',
        className: 'w-[7%] flex justify-start',
        type: 'icon',
        width: '0',
      },
    ],
    rows: 5,
    showHeaderControls: true,
    showSearchField: true,
  };

  public readonly datatableColumns: TDatatableColumn<ICustomerTable, string>[] = [
    {
      id: 'name',
      isClientSideSearchable: true,
      isSortable: true,
    },
    {
      dataViewStrategy: (row: ICustomerTable) => row?.createdAt ?? '',
      id: 'created',
      isSortable: true,
    },
    {
      dataViewStrategy: (row: ICustomerTable) => String(row?.campaigns?.length ?? 0),
      id: 'campaigns',
      isSortable: true,
    },
    {
      dataViewStrategy: () => '',
      id: 'actions',
    },
  ];

  public datatableConfig: IDatatableConfiguration<ICustomerTable> = {};

  private readonly store = inject(CustomersStore);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly tableStateService: TableStateService = inject(TableStateService);
  private readonly tableStorageKey: string = this.tableStateService.buildKey('customers', 'datatable');

  private readonly menu = viewChild<MatMenu>('menu');

  constructor() {
    super();

    this.datatableConfig = {
      storageKey: this.tableStorageKey,
    };

    effect(() => {
      this.dataSource().data =
        this.customers()?.map((customer) => ({
          ...customer,
          createdAt: this.dateUtils.parseDateTimeFromApi(customer.createdAt ?? ''),
        })) ?? [];

      if (!this.store.loading$()) {
        this.loadedTableData.set(true);
      }

      if (this.store.newCustomer$()) {
        this.router.navigate(['/customers', this.store.newCustomer$()?.id]);
      }
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(() => {
        this.store.fetchCustomers();
      }),
    );
  }

  public onDeleteClick(event: Event, customer: ICustomer): void {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.customers.datatable.deleteCustomerMessage', {
          name: customer.name,
        }),
        title: this.translate.instant('pages.customers.datatable.deleteCustomer'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.store.deleteCustomer(customer.id);
        this.menu()?.closed.emit();
      });
  }

  public onRowClick(row: IRowClickEvent<ICustomerTable, string>): void {
    if (!this.permissionService.hasPermission(this.EPermissions.customerReadOwn)) {
      return;
    }

    const { event, row: customer } = row;

    const isNewTab = event?.metaKey || event?.ctrlKey;

    if (isNewTab) {
      WindowRedirectService.openUrlInNewTab(`/customers/${customer?.id}`);

      return;
    }

    this.isRedirecting.set(true);
    this.router.navigate(['/customers', customer?.id]);
  }
}
